import { UseQueryOptions, useMutation, useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { RemoteTermsRepo } from '@repository/terms/TermsRepo';
import { RemoteUserTermsAgreesRepo } from '@repository/userTermsAgrees/UserTermsAgreesRepo';
import { AgreeTermsThirdPartyPrivacyProvisionRq } from '@domain/rq/terms/AgreeTermsThirdPartyPrivacyProvisionRq';
import { ThirdPartyProvideTermsGetRq } from '@domain/rq/terms/ThirdPartyProvideTermsGetRq';
import { AgreePositionInformationRq } from '@domain/rq/terms/AgreePositionInformationRq';
import MatchTermsVO from '@domain/vo/terms/MatchTermVO';
import TermsVO from '@domain/vo/terms/TermsVO';
import {
  MATCH_TERMS_TYPE,
  resultSubmitTermsType,
  TERMS_TYPE,
  THIRD_PARTY_TERMS_TYPE,
} from '@domain/constant/termsType';

export const keys = ['terms'];
export const matchingAgreeCheckKeys = [...keys, 'matching', 'agreeCheck'];
const remoteTermsRepo = new RemoteTermsRepo();
const remoteUserTermsAgreesRepo = new RemoteUserTermsAgreesRepo();

const fetchTermsOne = async (termsSn?: number | null) => {
  if (!termsSn) return undefined;
  const { data } = await remoteTermsRepo.fetchTermsOne(termsSn);
  return data.terms;
};

// 매칭 프로필 약관 조회(지원현황, 내 프로필)
const fetchMatchTermsAndCheckAgreeProfile = async () => {
  const termsSort = [
    MATCH_TERMS_TYPE.MATCHING_PRIVACY_POLICY,
    MATCH_TERMS_TYPE.MATCHING_PRIVACY_PROVISION,
    MATCH_TERMS_TYPE.MATCHING_EVALUATION_PROVISION,
  ];

  const { data: checkData } = await remoteUserTermsAgreesRepo.fetchMatchAgreeInformation();
  const { data: termsData } = await remoteTermsRepo.fetchMatchTerms();

  if (termsData) termsData.terms.sort((a, b) => termsSort.indexOf(a.type) - termsSort.indexOf(b.type));

  return termsData.terms
    .filter((item) => {
      //이미 동의 받은 항목 제거
      switch (item.type) {
        case MATCH_TERMS_TYPE.MATCHING_PRIVACY_POLICY:
          return !checkData.privacyPolicyYn;
        case MATCH_TERMS_TYPE.MATCHING_PRIVACY_PROVISION:
          return !checkData.privacyProvisionYn;
        case MATCH_TERMS_TYPE.MATCHING_EVALUATION_PROVISION:
          return !checkData.evaluationProvisionYn;
        default:
          return true;
      }
    })
    .map((item) => {
      switch (item.type) {
        case MATCH_TERMS_TYPE.MATCHING_PRIVACY_POLICY:
          return new MatchTermsVO(item, true);
        case MATCH_TERMS_TYPE.MATCHING_PRIVACY_PROVISION:
        case MATCH_TERMS_TYPE.MATCHING_EVALUATION_PROVISION:
        default:
          return new MatchTermsVO(item, false);
      }
    });
};

//제 3자 동의 필수 값인 경우(추천 서비스 신청 약관)
const fetchMatchTermsAndCheckAgree = async () => {
  const termsSort = [MATCH_TERMS_TYPE.MATCHING_PRIVACY_PROVISION, MATCH_TERMS_TYPE.MATCHING_EVALUATION_PROVISION];

  const { data: checkData } = await remoteUserTermsAgreesRepo.fetchMatchAgreeInformation();
  const { data: termsData } = await remoteTermsRepo.fetchMatchTerms();

  if (termsData) termsData.terms.sort((a, b) => termsSort.indexOf(a.type) - termsSort.indexOf(b.type));

  return termsData.terms
    .filter((item) => {
      //이미 동의 받은 항목 제거
      switch (item.type) {
        case MATCH_TERMS_TYPE.MATCHING_PRIVACY_POLICY:
          return !checkData.privacyPolicyYn;
        case MATCH_TERMS_TYPE.MATCHING_PRIVACY_PROVISION:
          return !checkData.privacyProvisionYn;
        case MATCH_TERMS_TYPE.MATCHING_EVALUATION_PROVISION:
          return !checkData.evaluationProvisionYn;
        default:
          return true;
      }
    })
    .map((item) => new MatchTermsVO(item, true));
};

const saveMatchTerms = async (termsSnList: number[]) => {
  const { data } = await remoteUserTermsAgreesRepo.saveMatchTerms(termsSnList);
  return data.result;
};

const fetchProfileAgreeInformation = async () => {
  const { data } = await remoteUserTermsAgreesRepo.fetchMatchAgreeInformation();
  return data;
};

const fetchTermsResultPrivacyProvision = async (companySn: number, termsType?: resultSubmitTermsType) => {
  if (!termsType) return;
  const { data } = await remoteTermsRepo.fetchTermsResultPrivacyProvision(companySn, termsType);
  return data;
};

const fetchAgreeTermsResultPrivacyProvision = async (companySn: number, termsSn: number) => {
  const { data } = await remoteTermsRepo.fetchAgreeTermsResultPrivacyProvision(companySn, termsSn);
  return data;
};

const fetchTermsList = async (termType: TERMS_TYPE, termRequired?: boolean) => {
  const { data } = await remoteTermsRepo.fetchTermsList(termType);
  return data.terms.map((item) => new TermsVO(item, termRequired));
};

const fetchTermsThirdPartyPrivacyProvision = async (rq: ThirdPartyProvideTermsGetRq) => {
  const { data } = await remoteTermsRepo.fetchTermsThirdPartyPrivacyProvision(rq);
  return data;
};

const fetchAgreeTermsThirdPartyPrivacyProvision = async (rq: AgreeTermsThirdPartyPrivacyProvisionRq) => {
  const { data } = await remoteTermsRepo.fetchAgreeTermsThirdPartyPrivacyProvision(rq);
  return data;
};

const fetchTermsThirdPartyPrivacyProvisionAgreement = async (
  termsType: THIRD_PARTY_TERMS_TYPE,
  companyName: string,
) => {
  const { data } = await remoteTermsRepo.fetchTermsThirdPartyPrivacyProvisionAgreement(termsType, companyName);
  return data;
};

const fetchPositionAgreeInformation = async () => {
  const { data } = await remoteUserTermsAgreesRepo.fetchPositionAgreeInformation();
  return data;
};

const savePositionTerms = async (rq: AgreePositionInformationRq) => {
  const { data } = await remoteUserTermsAgreesRepo.savePositionTerms(rq);
  return data;
};

// useQuery ///
//약관 본문 조회
export function useTermsOne(termsSn?: number | null, isEnabled = true) {
  return useQuery([...keys, termsSn], () => fetchTermsOne(termsSn), {
    enabled: isEnabled && !!termsSn,
  });
}

export function useTerms({
  termType,
  termRequired,
  options,
}: {
  termType: TERMS_TYPE;
  termRequired?: boolean;
  options?: UseQueryOptions;
}) {
  return useQuery([...keys, termType], () => fetchTermsList(termType, termRequired), options as any);
}

export function useProfileAgreeInformation(enabled: boolean) {
  return useQuery(matchingAgreeCheckKeys, () => fetchProfileAgreeInformation(), {
    enabled,
  });
}

export function useMatchTermsAndCheckAgree(enabled: boolean) {
  return useQuery([...keys, 'marketing', 'recommendService'], fetchMatchTermsAndCheckAgree, {
    enabled,
  });
}

export function useMatchTermsAndCheckAgreeProfile(enabled: boolean) {
  return useQuery([...keys, 'marketing', 'profile'], fetchMatchTermsAndCheckAgreeProfile, {
    enabled,
  });
}

export function useTermsResultPrivacyProvision(companySn: number, termsType?: resultSubmitTermsType) {
  return useQuery(
    [...keys, 'result', 'privacyProvision', companySn, termsType],
    () => fetchTermsResultPrivacyProvision(companySn, termsType),
    {
      enabled: !!termsType,
    },
  );
}

export function useTermsThirdPartyPrivacyProvision(rq: ThirdPartyProvideTermsGetRq) {
  const { termsType, companyName, provideItems } = rq;
  return useQuery([...keys, 'third-party', rq], () => fetchTermsThirdPartyPrivacyProvision(rq), {
    enabled: !!(termsType && companyName && provideItems),
  });
}

export function usePositionAgreeInformation() {
  return useQuery([...keys, 'position'], fetchPositionAgreeInformation);
}

// useMutation ///

export function useSaveMatchTerms(onSuccess?: () => void) {
  return useMutation((termsSnList: number[]) => saveMatchTerms(termsSnList), {
    onSuccess,
    onError: (error: AxiosError) => {
      console.log(error.response?.data);
      return;
    },
  });
}

export function useAgreeTermsResultPrivacyProvision(onSuccess?: () => void) {
  return useMutation(
    (termsRq: { companySn: number; termsSn: number }) =>
      fetchAgreeTermsResultPrivacyProvision(termsRq.companySn, termsRq.termsSn),
    {
      onSuccess,
      onError: (error: AxiosError) => {
        console.log(error.response?.data);
        return;
      },
    },
  );
}

export function useTermsThirdPartyPrivacyProvisionAgreement() {
  return useMutation((termsRq: { termsType: THIRD_PARTY_TERMS_TYPE; companyName: string }) =>
    fetchTermsThirdPartyPrivacyProvisionAgreement(termsRq.termsType, termsRq.companyName),
  );
}

export function useAgreeTermsThirdPartyPrivacyProvision() {
  return useMutation((rq: AgreeTermsThirdPartyPrivacyProvisionRq) => fetchAgreeTermsThirdPartyPrivacyProvision(rq));
}

export function useSavePositionTerms(options?: UseQueryOptions) {
  return useMutation((rq: AgreePositionInformationRq) => savePositionTerms(rq), options as any);
}
